import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { IoRocketSharp } from "react-icons/io5";

export default function Deployments() {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logModal, setLogModal] = useState({ open: false, logs: "", title: "", loading: false });

  const loadDeployments = async () => {
    try {
      const res = await api.listDeployments();
      if (res.success && res.data) {
        setDeployments(res.data.deployments);
      }
    } catch (err) {
      console.error("Failed to load deployments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeployments();
    const interval = setInterval(loadDeployments, 5000);
    return () => clearInterval(interval);
  }, []);

  const viewLogs = async (depId, commitMsg) => {
    setLogModal({ open: true, logs: "", title: `Deployment #${depId} - ${commitMsg || "Logs"}`, loading: true });
    try {
      const res = await api.getDeploymentLogs(depId);
      if (res.success) {
        setLogModal((prev) => ({ ...prev, logs: res.data.logs || "No logs available.", loading: false }));
      }
    } catch (err) {
      setLogModal((prev) => ({ ...prev, logs: "Failed to load logs: " + err.message, loading: false }));
    }
  };

  const depColumns = [
    { name: "id", align: "left" },
    { name: "site", align: "left" },
    { name: "trigger", align: "center" },
    { name: "commit", align: "left" },
    { name: "status", align: "center" },
    { name: "duration", align: "center" },
    { name: "date", align: "center" },
    { name: "actions", align: "right" },
  ];

  const depRows = deployments.map((dep) => ({
    id: (
      <VuiTypography variant="button" color="white" fontWeight="bold">
        #{dep.id}
      </VuiTypography>
    ),
    site: (
      <Link to={`/sites/${dep.siteId}`} style={{ textDecoration: "none" }}>
        <VuiTypography variant="button" color="info" fontWeight="medium">
          {dep.siteName}
        </VuiTypography>
      </Link>
    ),
    trigger: (
      <VuiBadge
        color="dark"
        variant="standard"
        size="sm"
        badgeContent={dep.triggerType === "webhook" ? "GIT PUSH" : "MANUAL"}
      />
    ),
    commit: (
      <VuiBox display="flex" flexDirection="column">
        <VuiTypography variant="button" color="white">
          {dep.commitMessage ? (dep.commitMessage.length > 35 ? dep.commitMessage.slice(0, 35) + "..." : dep.commitMessage) : "Initial deploy"}
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          {dep.commitSha ? dep.commitSha.slice(0, 7) : "HEAD"} ({dep.branch})
        </VuiTypography>
      </VuiBox>
    ),
    status: (
      <VuiBadge
        color={dep.status === "live" ? "success" : dep.status === "building" ? "info" : dep.status === "failed" ? "error" : "warning"}
        variant="standard"
        size="sm"
        badgeContent={dep.status.toUpperCase()}
      />
    ),
    duration: (
      <VuiTypography variant="caption" color="text">
        {dep.durationMs ? `${(dep.durationMs / 1000).toFixed(1)}s` : "-"}
      </VuiTypography>
    ),
    date: (
      <VuiTypography variant="caption" color="text">
        {new Date(dep.createdAt).toLocaleString()}
      </VuiTypography>
    ),
    actions: (
      <VuiButton
        variant="text"
        color="info"
        size="small"
        onClick={() => viewLogs(dep.id, dep.commitMessage)}
      >
        View Logs
      </VuiButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            Deployments
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Global deployment pipeline activity across all hosted sites
          </VuiTypography>
        </VuiBox>

        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Build History
            </VuiTypography>
            <VuiButton variant="text" color="info" size="small" onClick={loadDeployments}>
              Refresh
            </VuiButton>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={8}>
              <CircularProgress color="info" />
            </VuiBox>
          ) : deployments.length === 0 ? (
            <VuiBox py={8} textAlign="center">
              <IoRocketSharp size="48px" color="#0075ff" style={{ marginBottom: "16px" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                No Deployments Yet
              </VuiTypography>
              <VuiTypography variant="button" color="text">
                When you deploy a site, all build and release logs will be recorded here.
              </VuiTypography>
            </VuiBox>
          ) : (
            <Table columns={depColumns} rows={depRows} />
          )}
        </Card>
      </VuiBox>

      {/* Build Logs Dialog */}
      <Dialog
        open={logModal.open}
        onClose={() => setLogModal({ ...logModal, open: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#070d24",
            backgroundImage: "none",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "16px",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>
          {logModal.title}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.08)" }}>
          {logModal.loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress color="info" />
            </VuiBox>
          ) : (
            <VuiBox
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                backgroundColor: "#030614",
                p: 2,
                borderRadius: "8px",
                overflowX: "auto",
                maxHeight: "500px",
                color: "#00e676",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {logModal.logs}
            </VuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <VuiButton color="info" onClick={() => setLogModal({ ...logModal, open: false })}>
            Close
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
